/**
 * Step 0 — mode chooser landing.
 *
 * Renders 3 clickable cards (one per Step0Mode). Picking a card
 * dispatches `actions.setStepMode(0, mode)` via the supplied
 * `onSelectMode` callback. Default state — visible only when
 * `selectedMode === null` on the reducer's Step 0 slot.
 *
 * Mirrors the legacy `ItemDefinitionModeModal` (~3 options); ported
 * inline to v2 so the wizard stays inside the workflow tab.
 */
import React from 'react';
import type { Step0Mode } from '../../state/workflowV2Types';
import { Icon } from '../../../../components/ui/Icon';
import styles from './ModeChooserCard.module.css';

interface ModeDescriptor {
  mode: Step0Mode;
  iconName: string;
  title: string;
  description: string;
}

// Mode-chooser cards render their glyph through the shared <Icon> at 32px.
const MODES: ModeDescriptor[] = [
  {
    mode: 'automatic',
    iconName: 'upload',
    title: 'Automatic Analysis',
    description:
      'Upload technical documents (PDF, DOCX, images). The analysis agent extracts a draft Item Definition you can review and revise.',
  },
  {
    mode: 'manual',
    iconName: 'edit',
    title: 'Manual Definition',
    description:
      'Write the Item Definition by hand using the ISO/SAE 21434 template. The agent uses your content directly, skipping document extraction.',
  },
  {
    mode: 'diagram',
    iconName: 'diagram',
    title: 'Import Architecture Diagram',
    description:
      'Pick an existing architecture diagram for this project. Its components and connections seed the Item Definition.',
  },
];

export interface ModeChooserCardProps {
  onSelectMode: (mode: Step0Mode) => void;
}

export default function ModeChooserCard({ onSelectMode }: ModeChooserCardProps) {
  return (
    <section className={styles.chooser}>
      <div className={styles.grid}>
        {MODES.map(({ mode, iconName, title, description }) => (
          <button
            key={mode}
            type="button"
            className={styles.card}
            onClick={() => onSelectMode(mode)}
          >
            <div className={styles.cardIcon}><Icon name={iconName} size={32} /></div>
            <div className={styles.cardTitle}>{title}</div>
            <div className={styles.cardDesc}>{description}</div>
          </button>
        ))}
      </div>
      <footer className={styles.about}>
        <p>
          The <strong>Item Definition</strong> is the foundation of your TARA analysis. It captures
          the system under study — its architecture, boundaries, operational context, and
          security-relevant assumptions — in an ISO/SAE 21434-compliant structure. Downstream
          agents (asset identification, threat analysis, risk assessment, and beyond) consume this
          definition as their primary input.
        </p>
        <p>
          Pick the input method that best fits the artifacts you have. In Step 1 you'll review and
          revise the generated Item Definition before approving it; the agents only start running
          on your data once you advance past Step 1.
        </p>
      </footer>
    </section>
  );
}
